import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Loader2, ImageOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import client from "@/lib/api";

/**
 * Fetch the anonymized image with Authorization Bearer.
 * Handles 202 (processing) → retry with backoff (2s, 5s, 10s).
 * Never falls back to the original image_url (RGPD).
 */
async function fetchAnonymizedImage(objectId, { signal } = {}) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const token =
    typeof window !== "undefined"
      ? window.sessionStorage?.getItem("salnes_token")
      : null;
  const url = `${backend}/api/incidencias/${objectId}/image`;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  });
  if (res.ok) {
    const blob = await res.blob();
    return { status: "loaded", blobUrl: URL.createObjectURL(blob) };
  }
  if (res.status === 202 || res.status === 503) {
    return { status: "processing" };
  }
  if (res.status === 404) return { status: "no-image" };
  return { status: "error" };
}

export default function GeoDetailDrawer({ detail, open, onClose, role }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const [imgState, setImgState] = useState("idle"); // idle|loading|processing|loaded|error|no-image
  const [blobUrl, setBlobUrl] = useState(null);
  // iter46 · UX quick-reveal para ADMIN/SUPERVISOR (mismo patrón que
  //   `IncidenciaDetailDrawer`). Hover: ephemeral (no audit). Click:
  //   persistente + audit_events. Roles inferiores: sin acceso.
  const canQuickReveal = role === "ADMIN" || role === "SUPERVISOR";
  const [showOriginal, setShowOriginal] = useState(false);
  const [hoverReveal, setHoverReveal] = useState(false);
  // iter46b · Ventana de 500ms tras "click de deshacer" que bloquea la
  //   reactivación del hover reveal (evita flicker).
  const [explicitlyHidden, setExplicitlyHidden] = useState(false);
  const isRevealed = showOriginal || hoverReveal;
  const handleImageClick = () => {
    if (showOriginal) {
      setShowOriginal(false);
      setHoverReveal(false);
      setExplicitlyHidden(true);
      setTimeout(() => setExplicitlyHidden(false), 500);
    } else {
      setShowOriginal(true);
    }
  };
  const abortRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Reset on drawer open / object change
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setImgState(open && detail?.id ? "loading" : "idle");

    if (!open || !detail?.id) return undefined;

    const controller = new AbortController();
    abortRef.current = controller;
    const backoff = [0, 2000, 5000, 10000]; // 4 attempts: immediate, +2s, +5s, +10s

    let attempt = 0;
    let cancelled = false;

    const run = async () => {
      while (attempt < backoff.length && !cancelled) {
        if (attempt > 0) {
          await new Promise((r) => {
            timeoutRef.current = setTimeout(r, backoff[attempt]);
          });
          if (cancelled) return;
        }
        try {
          const out = await fetchAnonymizedImage(detail.id, {
            signal: controller.signal,
          });
          if (cancelled) return;
          if (out.status === "loaded") {
            setBlobUrl(out.blobUrl);
            setImgState("loaded");
            return;
          }
          if (out.status === "no-image") {
            setImgState("no-image");
            return;
          }
          if (out.status === "processing") {
            setImgState("processing");
            attempt += 1;
            continue;
          }
          setImgState("error");
          return;
        } catch (e) {
          if (e?.name === "AbortError") return;
          setImgState("error");
          return;
        }
      }
      // Exhausted retries; keep processing state
    };
    run();

    return () => {
      cancelled = true;
      controller.abort();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [open, detail?.id]);

  useEffect(() => {
    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  useEffect(() => {
    // Reset revelación al cambiar de detección o cerrar el drawer.
    setShowOriginal(false);
    setHoverReveal(false);
    setExplicitlyHidden(false);
  }, [detail?.id, open]);

  // iter46 · audit event sólo cuando el reveal es PERSISTENTE (click).
  useEffect(() => {
    if (!detail?.id) return;
    if (canQuickReveal && showOriginal) {
      client.post(`/incidencias/${detail.id}/reveal-image`).catch(() => {});
    }
  }, [detail?.id, canQuickReveal, showOriginal]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white border-l hairline shadow-2xl z-40 flex flex-col animate-fade-up"
      data-testid="geo-detail-drawer"
    >
      <div className="flex items-center justify-between h-14 px-5 border-b hairline">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">
            {t("fase2.detection")}
          </div>
          <div className="font-serif text-lg">#{detail?.id || "—"}</div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          data-testid="drawer-close"
        >
          <X size={18} />
        </Button>
      </div>
      <div className="p-5 overflow-y-auto space-y-4 flex-1">
        <ImageBlock
          state={imgState}
          blobUrl={blobUrl}
          lang={lang}
          label={t("fase2.photo_blur_notice")}
          originalUrl={detail?.image_url}
          canQuickReveal={canQuickReveal}
          hoverReveal={hoverReveal}
          showOriginal={showOriginal}
          explicitlyHidden={explicitlyHidden}
          onHoverEnter={() => setHoverReveal(true)}
          onHoverLeave={() => setHoverReveal(false)}
          onToggleClick={handleImageClick}
          hoverHint={t("fase4.inc_hover_hint")}
          originalBanner={t("fase4.inc_original_banner")}
        />

        <DetailRow label={t("fase2.status")} value={detail?.status} />
        <DetailRow
          label={detail?.overflow ? t("fase2.overflow") : t("fase2.observation")}
          value={detail?.overflow ? "✔" : "—"}
        />
        <DetailRow label="Jetson ID" value={detail?.jetson_id} />
        <DetailRow
          label={t("fase2.concello")}
          value={
            detail?.municipality && detail.municipality !== "OSalnes"
              ? detail.municipality
              : "O Salnés"
          }
        />
        <DetailRow
          label={t("fase2.last_update")}
          value={detail?.detection_date}
        />
        <DetailRow
          label="Lat / Lon"
          value={
            detail?.latitude && detail?.longitude
              ? `${detail.latitude.toFixed(5)} / ${detail.longitude.toFixed(5)}`
              : detail?.lat && detail?.lon
                ? `${detail.lat.toFixed(5)} / ${detail.lon.toFixed(5)}`
                : "—"
          }
        />
      </div>
    </div>
  );
}

function ImageBlock({
  state,
  blobUrl,
  lang,
  label,
  originalUrl,
  canQuickReveal,
  hoverReveal,
  showOriginal,
  explicitlyHidden,
  onHoverEnter,
  onHoverLeave,
  onToggleClick,
  hoverHint,
  originalBanner,
}) {
  if (state === "loaded" && blobUrl) {
    const isRevealed = (showOriginal || hoverReveal) && !!originalUrl;
    return (
      <div
        className="relative rounded-xl overflow-hidden border hairline"
        data-testid="drawer-image-loaded"
        data-reveal-mode={
          showOriginal ? "persistent" : hoverReveal ? "hover" : "blur"
        }
        onMouseEnter={
          canQuickReveal && originalUrl && !explicitlyHidden
            ? onHoverEnter
            : undefined
        }
        onMouseLeave={canQuickReveal && originalUrl ? onHoverLeave : undefined}
        onClick={canQuickReveal && originalUrl ? onToggleClick : undefined}
        style={{
          cursor: canQuickReveal && originalUrl ? "zoom-in" : "default",
        }}
      >
        <img
          src={isRevealed ? originalUrl : blobUrl}
          alt={isRevealed ? "original" : "anonymized"}
          className="w-full h-56 object-cover pointer-events-none"
          loading="lazy"
          draggable={false}
        />
        {canQuickReveal && hoverReveal && !showOriginal && (
          <div
            className="absolute bottom-2 right-2 text-[10px] uppercase tracking-[0.15em] bg-black/70 text-white px-2 py-1 rounded pointer-events-none"
            data-testid="drawer-hover-hint"
          >
            {hoverHint}
          </div>
        )}
        {showOriginal && (
          <div
            className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.15em] bg-salnes-coral text-white px-2 py-1 rounded pointer-events-none font-medium"
            data-testid="drawer-original-banner"
          >
            {originalBanner}
          </div>
        )}
        {!isRevealed && (
          <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[11px] px-3 py-2 pointer-events-none">
            {label}
          </div>
        )}
      </div>
    );
  }
  if (state === "loading") {
    return (
      <div
        className="rounded-xl border hairline bg-salnes-sand/30 h-40 flex flex-col items-center justify-center text-salnes-stone text-xs gap-2"
        data-testid="drawer-image-loading"
      >
        <Loader2 size={18} className="animate-spin" />
        {lang === "es" ? "Cargando imagen…" : "Cargando imaxe…"}
      </div>
    );
  }
  if (state === "processing") {
    return (
      <div
        className="rounded-xl border border-amber-200 bg-amber-50 h-40 flex flex-col items-center justify-center text-amber-900 text-xs gap-2 px-4 text-center"
        data-testid="drawer-image-processing"
      >
        <Loader2 size={18} className="animate-spin text-amber-600" />
        <div className="font-medium">
          {lang === "es"
            ? "Imagen en proceso por IA (Gemini Vision)"
            : "Imaxe en procesado por IA (Gemini Vision)"}
        </div>
        <div className="text-[10px] text-amber-700/80">
          {lang === "es"
            ? "Actualiza en unos segundos."
            : "Actualiza en uns segundos."}
        </div>
      </div>
    );
  }
  return (
    <div
      className="rounded-xl border hairline bg-neutral-50 h-40 flex flex-col items-center justify-center text-salnes-stone text-xs gap-2"
      data-testid={
        state === "no-image" ? "drawer-image-none" : "drawer-image-error"
      }
    >
      <ImageOff size={18} className="text-salnes-stone/60" />
      {lang === "es"
        ? "Sin imagen disponible"
        : "Sen imaxe dispoñible"}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm border-b hairline pb-2 last:border-none">
      <span className="text-salnes-stone text-[11px] uppercase tracking-[0.18em] pt-0.5">
        {label}
      </span>
      <span className="text-salnes-ink text-right break-all">
        {value == null || value === "" ? "—" : String(value)}
      </span>
    </div>
  );
}
